import { client } from '../../apiClient';
import { UserDataResponse } from '../../../models/admin/users/UserDataResponse';
import { UsersDataPageResponse } from '../../../models/admin/users/UsersDataPageResponse';
import { UserAccountData } from '../../../../pages/admin/users/edit/models/UserAccountData';
import { AccountDataResponse } from '../../../models/account/AccountData';
import { InvitedUserResponse } from '../../../models/admin/users/InvitedUserResponse';
import { UserTeamData } from '../../../../pages/settings/team/models/TeamResponse';
import { TokenData } from '../../../models/auth/TokenData';
import { AdminNoteResponse } from '../../../models/admin/users/AdminNoteResponse';

export const getAllUsers = async (): Promise<UserDataResponse[]> => {
  return client(true, true)
    .get<UserDataResponse[]>(`/admin/dashboard/all-users`)
    .then((response) => response.data)
    .catch((error) => { throw error; });
};

export type BillingCycleFilter = 'all' | 'monthly' | 'annual';

export const getUsersPage = async (filters?: {
  search?: string;
  accountType?: string;
  accountStatus?: string;
  accountOrigin?: 'all' | 'organic' | 'cs_assisted' | 'cs_assisted_brand24' | `api_client_${number}`;
  isCancelled?: boolean;
  confirmed?: boolean;
  noProject?: boolean;
  billingInterval?: BillingCycleFilter;
  sortBy?: string;
  sortDir?: 'ASC' | 'DESC';
  page?: number;
  pageSize?: number;
}): Promise<UsersDataPageResponse> => {
  const params = new URLSearchParams();
  params.set('page', String(filters?.page ?? 1));
  params.set('pageSize', String(filters?.pageSize ?? 50));
  if (filters?.search) params.set('search', filters.search);
  if (filters?.accountType) params.set('accountType', filters.accountType);
  if (filters?.accountStatus) params.set('accountStatus', filters.accountStatus);
  if (filters?.accountOrigin && filters.accountOrigin !== 'all') params.set('accountOrigin', filters.accountOrigin);
  if (filters?.isCancelled !== undefined) params.set('isCancelled', String(filters.isCancelled));
  if (filters?.confirmed !== undefined) params.set('confirmed', String(filters.confirmed));
  if (filters?.noProject === true) params.set('noProject', 'true');
  if (filters?.billingInterval && filters.billingInterval !== 'all') params.set('billingInterval', filters.billingInterval);
  if (filters?.sortBy) params.set('sortBy', filters.sortBy);
  if (filters?.sortDir) params.set('sortDir', filters.sortDir);

  return client(true, true)
    .get<UsersDataPageResponse>(`/admin/dashboard/all-users-page?${params}`)
    .then((response) => response.data)
    .catch((error) => { throw error; });
};

export const addAdminNote = async (accountId: number, content: string): Promise<AdminNoteResponse> => {
  return client(false, true)
    .post<AdminNoteResponse>(`/admin/users/admin-note/${accountId}`, { content })
    .then((response) => response.data);
};

export const updateAdminNote = async (
  accountId: number,
  noteId: number,
  content: string
): Promise<AdminNoteResponse> => {
  return client(false, true)
    .patch<AdminNoteResponse>(`/admin/users/admin-note/${accountId}/${noteId}`, { content })
    .then((response) => response.data);
};

export const deleteAdminNote = async (accountId: number, noteId: number): Promise<{ message: string }> => {
  return client(false, true)
    .delete<{ message: string }>(`/admin/users/admin-note/${accountId}/${noteId}`)
    .then((response) => response.data);
};

export const registerUser = async (formData: URLSearchParams): Promise<any> => {
  return client(false, true).post<any>('/admin/users/register-user', formData);
};

export const getAccountData = async (id: number): Promise<AccountDataResponse> => {
  return client(false, true)
    .get<AccountDataResponse>(`/admin/users/account-data/${id}`)
    .then((response) => response.data)
    .catch((error) => error);
};

export const getProjectsForAccount = async (id: number): Promise<{ id: number; label: string; created: string }[]> => {
  return client(false, true)
    .get<{ id: number; label: string; created: string }[]>(`/admin/users/projects/${id}`)
    .then((response) => response.data)
    .catch(() => []);
};

export const getInvitedUsers = async (id: number): Promise<InvitedUserResponse[]> => {
  return client(false, true)
    .get<InvitedUserResponse[]>(`/admin/users/invited-users/${id}`)
    .then((response) => response.data)
    .catch((error) => error);
};

export const changeUserAccountData = async (userAccount: UserAccountData): Promise<any> => {
  return client(false, true).post<any>('/admin/users/account-data', userAccount);
};

export const inviteUserByAdmin = async (
  id: number,
  userTeamData: UserTeamData
): Promise<InvitedUserResponse[]> => {
  return client(false, true)
    .post<InvitedUserResponse[]>(`/admin/users/invite-user/${id}`, userTeamData)
    .then((response) => response.data)
    .catch((error) => error);
};

export const resetPasswordByAdmin = async (email: string): Promise<{ message: string }> => {
  return client(false, true)
    .post<{ message: string }>(`/admin/users/reset-password`, { email })
    .then((response) => response.data);
};

export const impersonateUser = async (userId: number): Promise<TokenData> => {
  return client(true, true)
    .post<TokenData>(`/admin/users/impersonate/${userId}`)
    .then((response) => response.data);
};

export const getApiClients = async (filters?: {
  search?: string;
  accountType?: string;
  accountStatus?: string;
  isCancelled?: boolean;
}): Promise<{ id: number; name: string; accountCount: number }[]> => {
  const params = new URLSearchParams();
  if (filters?.search) params.set('search', filters.search);
  if (filters?.accountType) params.set('accountType', filters.accountType);
  if (filters?.accountStatus) params.set('accountStatus', filters.accountStatus);
  if (filters?.isCancelled !== undefined) params.set('isCancelled', String(filters.isCancelled));
  const qs = params.toString();
  return client(true, true)
    .get<{ id: number; name: string; accountCount: number }[]>(`/admin/dashboard/api-clients${qs ? `?${qs}` : ''}`)
    .then((response) => response.data)
    .catch(() => []);
};
